import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { SketchPicker } from 'react-color';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ScrapeTargetManager from '../components/ScrapeTargetManager';
import IngestRunMonitor from '../components/IngestRunMonitor';
import UsersManager from '../components/UsersManager';
import EmailDigestSender from '../components/EmailDigestSender';
import { AccordionSection } from '../components/ui/accordion-section';

const API_BASE = '';

type Tag = {
  id: string;
  name: string;
  color?: string;
};

const inputCls =
  'bg-[var(--surface)] border border-border rounded-[10px] px-3 py-2 text-[14px] text-[color:var(--text-primary)] outline-none focus:border-[var(--accent)]';

const AdminPage: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [tempColor, setTempColor] = useState('#ffffff');

  const protectedTags = ['Research', 'Policy', 'Business'];

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/tags`)
      .then((response) => setTags(response.data))
      .catch((error) => console.error('Error fetching tags:', error))
      .finally(() => setLoading(false));
  }, []);

  const handleDeleteTag = (tagId: string, tagName: string) => {
    if (protectedTags.includes(tagName)) {
      alert('This tag cannot be deleted.');
      return;
    }
    axios
      .delete(`${API_BASE}/api/tags/${tagId}`)
      .then(() => setTags((prev) => prev.filter((tag) => tag.id !== tagId)))
      .catch((err) => console.error('❌ Failed to delete tag:', err));
  };

  const handleCreateTag = () => {
    if (!newTag.trim()) {
      alert('Please enter a tag name.');
      return;
    }
    axios
      .post(`${API_BASE}/api/tags`, { name: newTag })
      .then((response) => {
        setTags((prev) => [...prev, response.data]);
        setNewTag('');
      })
      .catch((error) => {
        console.error('❌ Failed to create tag:', error);
        alert('Failed to create tag. It might already exist.');
      });
  };

  const handleTagClick = (tag: Tag) => {
    setSelectedTag(tag);
    setTempColor(tag.color || '#ffffff');
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedTag(null);
    setShowColorPicker(false);
  };

  const handleDialogSave = () => {
    if (!selectedTag) return;
    axios
      .put(`${API_BASE}/api/tags/${selectedTag.id}`, { name: selectedTag.name, color: tempColor })
      .then((response) => {
        setTags((prev) => prev.map((tag) => (tag.id === selectedTag.id ? response.data : tag)));
        handleCloseDialog();
      })
      .catch((error) => {
        console.error('Failed to update tag:', error);
        alert('Failed to update tag.');
      });
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 py-8">
      <h1 className="text-2xl font-semibold text-[color:var(--text-primary)]">Admin</h1>
      <p className="text-[13px] text-[color:var(--text-secondary)] mt-1">
        Manage tags, crawl URLs, and ingestion runs.
      </p>

      <AccordionSection title="Email Digest" defaultOpen>
        <EmailDigestSender />
      </AccordionSection>

      <AccordionSection title="Users" defaultOpen>
        <UsersManager />
      </AccordionSection>

      <AccordionSection title="Scrape URLs" defaultOpen>
        <ScrapeTargetManager />
      </AccordionSection>

      <AccordionSection title="Ingestion Runs" defaultOpen>
        <IngestRunMonitor />
      </AccordionSection>

      {/* ── Tag Manager (collapsed by default, at the bottom) ── */}
      <AccordionSection title="Tags">
        <p className="text-[13px] text-[color:var(--text-secondary)] mb-4">
          Tags applied to curated content. Protected tags ({protectedTags.join(', ')}) can't be deleted.
        </p>

        {loading ? (
          <p className="text-[color:var(--text-muted)]">Loading…</p>
        ) : tags.length === 0 ? (
          <p className="text-[color:var(--text-muted)]">No tags yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tags.map((tag) => (
              <Card key={tag.id} className="flex items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-4 w-4 rounded border border-border shrink-0"
                    style={{ backgroundColor: tag.color || 'var(--surface-hover)' }}
                  />
                  <span className="text-[15px] font-medium text-[color:var(--text-primary)] truncate">{tag.name}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => handleTagClick(tag)}>Edit</Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteTag(tag.id, tag.name)}
                    disabled={protectedTags.includes(tag.name)}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create tag */}
        <div className="mt-4 flex items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-[color:var(--text-secondary)]">New tag</span>
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
              placeholder="Tag name"
              className={inputCls + ' min-w-[220px]'}
            />
          </label>
          <Button onClick={handleCreateTag} disabled={!newTag.trim()}>Create tag</Button>
        </div>
      </AccordionSection>

      {/* ── Edit-tag dialog ─────────────────────────────────── */}
      {open && selectedTag && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={handleCloseDialog}
        >
          <Card className="w-full max-w-[440px] p-6 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[20px] font-semibold text-[color:var(--text-primary)]">Edit Tag</h3>

            <label className="flex flex-col gap-1">
              <span className="text-[13px] font-medium text-[color:var(--text-secondary)]">Tag name</span>
              <input
                type="text"
                value={selectedTag.name}
                onChange={(e) => setSelectedTag({ ...selectedTag, name: e.target.value })}
                className={inputCls}
              />
            </label>

            <div>
              <p className="mb-2 text-[13px] font-medium text-[color:var(--text-secondary)]">Tag color</p>
              <div className="flex items-center gap-3">
                <span
                  className="h-10 w-10 rounded-lg border-2 border-border cursor-pointer"
                  style={{ backgroundColor: tempColor }}
                  onClick={() => setShowColorPicker((s) => !s)}
                />
                <Button variant="secondary" size="sm" onClick={() => setShowColorPicker((s) => !s)}>
                  {showColorPicker ? 'Close' : 'Choose color'}
                </Button>
              </div>
              {showColorPicker && (
                <div className="mt-4">
                  <SketchPicker color={tempColor} onChange={(color) => setTempColor(color.hex)} />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleDialogSave}>Save</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
